import React, { useEffect, useState, useRef } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { MapPin, Loader2 } from 'lucide-react';

// Sabit SVG dünya haritası (tile/API key YOK, filigran YOK)
const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const NodeMap = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const [nodes, setNodes] = useState([]);
  const [groupedNodes, setGroupedNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hovered, setHovered] = useState(null); // { node, x, y }
  const wrapRef = useRef(null);

  const translations = {
    tr: { title: 'Node Lokasyonları', description: 'Dünya genelindeki Threat.live node\'ları', loading: 'Harita yükleniyor...', error: 'Node verileri yüklenemedi', nodeCount: 'Node Sayısı', datacenter: 'Veri Merkezi', sponsor: 'Sponsor', nodes: 'node' },
    en: { title: 'Node Locations', description: 'Threat.live nodes around the world', loading: 'Loading map...', error: 'Failed to load node data', nodeCount: 'Node Count', datacenter: 'Datacenter', sponsor: 'Sponsor', nodes: 'nodes' },
  };
  const txt = translations[language] || translations.en;

  const groupNodesByLocation = (rawNodes) => {
    const locationGroups = {};
    rawNodes.forEach((node) => {
      const locationKey = `${parseFloat(node.lat).toFixed(5)}_${parseFloat(node.lon).toFixed(5)}`;
      if (!locationGroups[locationKey]) {
        locationGroups[locationKey] = { nodes: [], datacenters: new Set(), datacenterNodes: {} };
      }
      locationGroups[locationKey].nodes.push(node);
      locationGroups[locationKey].datacenters.add(node.datacenter);
      locationGroups[locationKey].datacenterNodes[node.datacenter] = (locationGroups[locationKey].datacenterNodes[node.datacenter] || 0) + 1;
    });
    const grouped = [];
    for (const locationKey in locationGroups) {
      const group = locationGroups[locationKey];
      const groupedNode = { ...group.nodes[0] };
      groupedNode.nodeCount = group.nodes.length;
      groupedNode.originalIds = group.nodes.map((n) => n.id);
      groupedNode.datacenters = Array.from(group.datacenters);
      groupedNode.datacenterCounts = groupedNode.datacenters.map((dc) => group.datacenterNodes[dc]);
      const nodeWithSponsor = group.nodes.find((n) => n.sponsorImg && n.sponsorImg.trim() !== '');
      if (nodeWithSponsor) groupedNode.sponsorImg = nodeWithSponsor.sponsorImg;
      grouped.push(groupedNode);
    }
    return grouped;
  };

  useEffect(() => {
    const fetchNodes = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://api.threat.live/get_nodes');
        if (!response.ok) throw new Error('Failed to fetch nodes');
        const data = await response.json();
        setNodes(data);
        setGroupedNodes(groupNodesByLocation(data));
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNodes();
  }, []);

  const isDark = theme === 'dark';
  const bg = isDark ? '#0a0a0b' : '#f8fafc';
  const landFill = isDark ? '#1f2937' : '#e5e7eb';
  const landStroke = isDark ? '#374151' : '#cbd5e1';

  const uniqueCountries = [...new Set(nodes.map((n) => n.country).filter(Boolean))].length;

  const Shell = ({ children }) => (
    <div className="border border-border bg-card" data-testid="node-map">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground">{txt.title}</span>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground hidden md:inline">// {txt.description}</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="status-dot" />LIVE</span>
          <span className="text-border">|</span>
          <span><span className="text-foreground tabular">{nodes.length}</span> {txt.nodes}</span>
          {uniqueCountries > 0 && (<><span className="text-border">|</span><span><span className="text-foreground tabular">{uniqueCountries}</span> COUNTRIES</span></>)}
        </div>
      </div>
      {children}
    </div>
  );

  if (isLoading) {
    return (
      <Shell>
        <div className="h-[500px] bg-muted/20 flex items-center justify-center">
          <div className="flex items-center gap-2 text-muted-foreground font-mono text-xs uppercase tracking-wider">
            <Loader2 className="h-4 w-4 animate-spin" />{txt.loading}
          </div>
        </div>
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell>
        <div className="h-[500px] bg-muted/20 flex items-center justify-center">
          <p className="text-destructive font-mono text-xs uppercase tracking-wider">{txt.error}</p>
        </div>
      </Shell>
    );
  }

  const onEnter = (node) => (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    setHovered({ node, x: e.clientX - (r?.left || 0), y: e.clientY - (r?.top || 0) });
  };

  return (
    <Shell>
      <div ref={wrapRef} className="relative h-[500px] overflow-hidden" style={{ background: bg }}>
        <ComposableMap projection="geoMercator" projectionConfig={{ scale: 235, center: [65, 28] }} style={{ width: '100%', height: '100%' }}>
          <ZoomableGroup center={[65, 28]} zoom={1} minZoom={1} maxZoom={7}>
          <Geographies geography={geoUrl}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography key={geo.rsmKey} geography={geo} fill={landFill} stroke={landStroke} strokeWidth={0.4}
                style={{ default: { outline: 'none' }, hover: { fill: landStroke, outline: 'none' }, pressed: { outline: 'none' } }} />
            ))}
          </Geographies>

          {groupedNodes.map((node) => {
            const lon = parseFloat(node.lon);
            const lat = parseFloat(node.lat);
            if (Number.isNaN(lon) || Number.isNaN(lat)) return null;
            const badgeW = node.nodeCount >= 10 ? 18 : 14;
            return (
              <Marker key={node.id} coordinates={[lon, lat]} onMouseEnter={onEnter(node)} onMouseMove={onEnter(node)} onMouseLeave={() => setHovered(null)} style={{ cursor: 'pointer' }}>
                <circle r={9} fill="rgba(16,185,129,0.22)">
                  <animate attributeName="r" values="7;13;7" dur="2.2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.5;0.08;0.5" dur="2.2s" repeatCount="indefinite" />
                </circle>
                <rect x={-6} y={-6} width={12} height={12} rx={2} fill="#10b981" stroke="rgba(16,185,129,0.9)" strokeWidth={1.5} style={{ filter: 'drop-shadow(0 0 6px rgba(16,185,129,0.6))' }} />
                <polyline points="-3,0 -1,2.5 3,-3" fill="none" stroke="#0b1b14" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
                {node.nodeCount > 1 && (
                  <g transform="translate(5,-10)">
                    <rect x={0} y={0} width={badgeW} height={12} rx={2} fill="#0f172a" stroke="rgba(16,185,129,0.6)" strokeWidth={0.75} />
                    <text x={badgeW / 2} y={9} textAnchor="middle" fontSize={9} fontWeight="700" fill="#10b981" style={{ fontFamily: 'monospace' }}>×{node.nodeCount}</text>
                  </g>
                )}
              </Marker>
            );
          })}
          </ZoomableGroup>
        </ComposableMap>

        {hovered && (
          <div className="pointer-events-none absolute z-20 rounded-md border border-emerald-500/40 bg-zinc-900/95 px-3 py-2 text-xs text-zinc-100 shadow-lg"
            style={{ left: Math.min(hovered.x + 12, (wrapRef.current?.clientWidth || 500) - 190), top: Math.max(hovered.y - 10, 8), maxWidth: 200 }}>
            <div className="font-semibold">{hovered.node.city}{hovered.node.city && hovered.node.country ? ', ' : ''}{hovered.node.country}</div>
            <div className="text-emerald-400 mt-0.5">{txt.nodeCount}: {hovered.node.nodeCount}</div>
            {hovered.node.nodeCount > 1
              ? hovered.node.datacenters.map((dc, i) => (<div key={dc} className="text-zinc-400">{dc}: {hovered.node.datacenterCounts[i]} {txt.nodes}</div>))
              : <div className="text-zinc-400">{hovered.node.datacenter}</div>}
            {hovered.node.sponsorImg && (<img src={hovered.node.sponsorImg} alt="" className="mt-1 max-h-8 max-w-[90px]" onError={(e) => { e.target.style.display = 'none'; }} />)}
          </div>
        )}
      </div>
    </Shell>
  );
};

export default NodeMap;
